import React, {useEffect, useState} from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Checkbox from '@mui/material/Checkbox';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Link from '@mui/material/Link';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import ClearingBanner from '../form/ClearingBanner';
import FormFooter from '../common/FormFooter';
import Select from '../common/Select';
import Input from '../common/Input';
import Spinner from '../common/Spinner';

import {postIdPhone} from '../../api/api';
import useScrollTop from '../../hooks/useScrollTop';
import {formSteps, validations} from '../../constants/form';
import {doValidation} from '../../helpers/form-validation';
import {formatDateInput} from '../../helpers/format-helper';
import {useDataProviderState, useDataProviderActions} from '../../providers/DataProvider';
import HighlightBox from "../common/HighlightBox.jsx";
import clearingImg from "../../assets/img/clearing.png";
import {theme} from "../../App.jsx";
import {TrackGoogleAnalyticsEvent} from '../../google-analytics/gaTracker.js';

export default function IdAndPhoneStep() {

    const {data, loading, initialData} = useDataProviderState();
    const {
        nextStep,
        saveData,
        setUserData,
        setUid,
        setLoading,
        setErrorMessage,
        setErrorPage
    } = useDataProviderActions();

    const [formData, setFormData] = useState(data[formSteps.ID_PHONE]);
    const [errors, setErrors] = useState({});

    const [open, setOpen] = useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const formValidations = validations[formSteps.ID_PHONE];

    useScrollTop();

    TrackGoogleAnalyticsEvent("IdAndPhoneStep");

    function updateErrors(event, name) {
        if (name === 'rut') {
            setErrors({
                ...errors,
                [name]: doValidation(event, formValidations[name]),
            });
        } else {
            setErrors({
                ...errors,
                [name]: doValidation(event, formValidations[name]),
                rut: isRutVisible() ? errors.rut : ''
            });
        }
    }

    function handleFieldChange(event) {
        let {value, name, type} = event.target;

        if (name === 'userFechaNac') {
            value = formatDateInput(value);
        }

        if (formValidations[name].regexReplace) {
            if (formValidations[name].regexReplace.test(value)) {
                setFormData({...formData, [name]: value});
            }
        } else {
            setFormData({...formData, [name]: value});
        }

        if (errors[name] || type === 'select-one') {
            updateErrors(event, name);
        }
    }

    function handleFieldBlur(event) {
        const {name} = event.target;
        updateErrors(event, name);
    }

    function handleCheckboxChange(event) {
        const {checked, name} = event.target;

        setFormData({...formData, [name]: checked});
        updateErrors(event, name);
    }

    function isRutVisible() {
        return ['4', '5'].includes(formData.ocupacion);
    }

    function isFormValid() {
        const updatedErrors = {
            nombre: doValidation({target: {value: formData.nombre}}, formValidations.nombre),
            apellido: doValidation({target: {value: formData.apellido}}, formValidations.apellido),
            ci: doValidation({target: {value: formData.ci}}, formValidations.ci),
            userFechaNac: doValidation({target: {value: formData.userFechaNac}}, formValidations.userFechaNac),
            sexo: doValidation({target: {value: formData.sexo}}, formValidations.sexo),
            ocupacion: doValidation({target: {value: formData.ocupacion}}, formValidations.ocupacion),
            rut: isRutVisible() ? doValidation({target: {value: formData.rut}}, formValidations.rut) : '',
            antiguedad: doValidation({target: {value: formData.antiguedad}}, formValidations.antiguedad),
            ingresos: doValidation({target: {value: formData.ingresos}}, formValidations.ingresos),
            email: doValidation({target: {value: formData.email}}, formValidations.email),
            telMovil: doValidation({target: {value: formData.telMovil}}, formValidations.telMovil),
            hasReciboSueldo: doValidation({target: {value: formData.hasReciboSueldo}}, formValidations.hasReciboSueldo),
            agreeTerms: doValidation({
                target: {
                    checked: formData.agreeTerms,
                    type: 'checkbox'
                }
            }, formValidations.agreeTerms),
        };

        setErrors(updatedErrors);

        return !Object.values(updatedErrors).some(Boolean);
    }

    async function handleNext() {
        if (isFormValid()) {
            setLoading(true);
            setErrors({});
            const response = await postIdPhone(formData);

            if (response?.success) {
                saveData(formData, formSteps.ID_PHONE);
                saveData({userFechaNac: formData.userFechaNac}, formSteps.SMS_CODE);
                setUserData(response.data);

                if (response.data?.status !== 'rojo') {
                    setUid(response.data.uid);
                    history.pushState(null, null, `#${response.data.uid}`);
                    nextStep();
                } else {
                    setErrorPage(response.data.statusMessage);
                }
            } else if (response?.errors) {
                setErrors(response.errors);
            } else {
                setErrorMessage();
            }

            setLoading(false);
        }
    }

    return (
        <Container component="section" maxWidth="sm">

            {/*<ClearingBanner/>*/}

            <Box sx={{pb: 2}}>
                <HighlightBox>
                    <Box sx={{display: 'flex'}}>
                        <Box sx={{py: 2}}>
                            <Box className="color-primary" sx={{pl: 3, pr: 3, pb: 0, fontSize: '1.3rem'}}>
                                <b>Te damos la bienvenido a Crédito de Valor, obtené tu préstamo en minutos y retíralo en cualquier Abitab o Redpagos al instante.</b>
                            </Box>
                        </Box>
                    </Box>
                </HighlightBox>
            </Box>

            <Box as="h3" sx={{px: 1, pl: 1, textAlign: 'left'}} className="color-primary">
                Te vamos a pedir algunos datos:
            </Box>

            <Grid container spacing={{xs: 0, sm: 2}}>
                <Grid item sm={6} xs={12} sx={{pb: 1}}>
                    <Input
                        label="Nombre"
                        name="nombre"
                        formData={formData}
                        errors={errors}
                        formValidations={formValidations}
                        handleFieldChange={handleFieldChange}
                        handleFieldBlur={handleFieldBlur}
                    />
                </Grid>

                <Grid item sm={6} xs={12} sx={{pb: 1}}>
                    <Input
                        label="Apellido"
                        name="apellido"
                        formData={formData}
                        errors={errors}
                        formValidations={formValidations}
                        handleFieldChange={handleFieldChange}
                        handleFieldBlur={handleFieldBlur}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={{xs: 0, sm: 2}}>
                <Grid item sm={12} xs={12} sx={{pb: 1}}>
                    <Input
                        sx={{py: 1}}
                        label="Cédula de intentidad (sin puntos ni guiones)"
                        name="ci"
                        type="tel"
                        placeholder="Ej: 41237895"
                        formData={formData}
                        errors={errors}
                        formValidations={formValidations}
                        handleFieldChange={handleFieldChange}
                        handleFieldBlur={handleFieldBlur}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={{xs: 0, sm: 2}}>
                <Grid item sm={6} xs={12} sx={{pb: 1}}>
                    <Input
                        type="tel"
                        label="Fecha de nacimiento"
                        name="userFechaNac"
                        placeholder="dd/mm/aaaa"
                        formData={formData}
                        errors={errors}
                        formValidations={formValidations}
                        handleFieldChange={handleFieldChange}
                        handleFieldBlur={handleFieldBlur}
                    />
                </Grid>

                <Grid item sm={6} xs={12} sx={{pb: 1}}>
                    <Select
                        label="Género"
                        name="sexo"
                        formData={formData}
                        onChange={handleFieldChange}
                        errors={errors}
                        formValidations={formValidations}
                        initialData={initialData}
                        handleFieldChange={handleFieldChange}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={{xs: 0, sm: 2}}>
                <Grid item sm={6} xs={12} sx={{pb: 1}}>
                    <Select
                        label="Actividad Laboral"
                        name="ocupacion"
                        formData={formData}
                        onChange={handleFieldChange}
                        errors={errors}
                        formValidations={formValidations}
                        initialData={initialData}
                        handleFieldChange={handleFieldChange}
                    />
                </Grid>

                <Grid item sm={6} xs={12} sx={{pb: 1}}>
                    <Select
                        label="Antigüedad Laboral"
                        name="antiguedad"
                        formData={formData}
                        onChange={handleFieldChange}
                        errors={errors}
                        formValidations={formValidations}
                        initialData={initialData}
                        handleFieldChange={handleFieldChange}
                    />
                </Grid>
            </Grid>

            {
                isRutVisible() ? (<Input
                    label="RUT"
                    name="rut"
                    type="tel"
                    formData={formData}
                    errors={errors}
                    formValidations={formValidations}
                    handleFieldChange={handleFieldChange}
                    handleFieldBlur={handleFieldBlur}
                />) : null
            }

            <Grid container spacing={{xs: 0, sm: 2}}>
                <Grid item sm={12} xs={12} sx={{pb: 1}}>
                    <Input
                        label="¿Cuál es tu ingreso líquido mensual?"
                        name="ingresos"
                        type="tel"
                        placeholder="Ej: 15.000"
                        formData={formData}
                        errors={errors}
                        formValidations={formValidations}
                        handleFieldChange={handleFieldChange}
                        handleFieldBlur={handleFieldBlur}
                    />
                </Grid>
            </Grid>

            <FormControl sx={{pt: 1, pl: 2}}>
                <FormLabel id="hasReciboSueldo-label">¿Tenés recibo de sueldo?</FormLabel>
                <RadioGroup
                    row
                    aria-labelledby="hasReciboSueldo-label"
                    name="hasReciboSueldo"
                    value={formData.hasReciboSueldo}
                    onChange={handleFieldChange}
                    required>
                    <FormControlLabel
                        value={true}
                        control={<Radio/>}
                        label="Si"
                        sx={{
                            '& .MuiSvgIcon-root': {
                                color: theme.palette.primary.main
                            }
                        }}
                    />
                    <FormControlLabel
                        value={false}
                        control={<Radio/>}
                        label="No"
                        sx={{
                            '& .MuiSvgIcon-root': {
                                color: theme.palette.primary.main
                            }
                        }}
                    />
                </RadioGroup>
            </FormControl>
            <FormHelperText sx={{
                mt: 0,
                pt: 0,
                pl: 2
            }} error={!!errors.hasReciboSueldo}>{errors.hasReciboSueldo}</FormHelperText>

            <Grid container spacing={{xs: 0, sm: 2}}>
                <Grid item sm={12} xs={12} sx={{pb: 1}}>
                    <Input
                        type="tel"
                        label="¿Cuál es tu celular?"
                        name="telMovil"
                        placeholder="Ej: 099000000"
                        formData={formData}
                        errors={errors}
                        formValidations={formValidations}
                        handleFieldChange={handleFieldChange}
                        handleFieldBlur={handleFieldBlur}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={{xs: 0, sm: 2}}>
                <Grid item sm={12} xs={12} sx={{pb: 1}}>
                    <Input
                        label="Correo electrónico"
                        name="email"
                        formData={formData}
                        errors={errors}
                        formValidations={formValidations}
                        handleFieldChange={handleFieldChange}
                        handleFieldBlur={handleFieldBlur}
                    />
                </Grid>
            </Grid>

            <FormControlLabel
                sx={{pt: 1, pl: 2}}
                control={
                    <Checkbox
                        checked={formData.agreeTerms}
                        onChange={handleCheckboxChange}
                        name="agreeTerms"
                        color="primary"
                        sx={{
                            '& .MuiSvgIcon-root': {
                                color: theme.palette.primary.main
                            }
                        }}
                    />
                }
                label={
                    <div className="color-tertiary">Estoy de acuerdo con los <Link href="#" onClick={handleOpen} underline="hover">
                        {'términos y condiciones'}
                    </Link> de Crédito de Valor. *</div>}
            />
            <FormHelperText sx={{mt: 0, pt: 0, pl: 2}} error={!!errors.agreeTerms}>{errors.agreeTerms}</FormHelperText>

            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {"Términos y condiciones"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description" component={'span'}>
                        <p className="line-h-16">Moneda: Pesos uruguayos – Monto máximo y mínimo: entre $200.000 y $5.000.
                            Plazos: de 6 a 36 meses.</p>
                        <p className="line-h-16">TEA (Tasa Efectiva Anual): entre 70% y 115% + IVA, a determinar antes del otorgamiento del
                            crédito, sujeto a: análisis crediticio, condiciones del préstamo y mercado, todo en marco de la
                            ley de usura y concordantes.</p>
                        <p className="line-h-16">El capital prestado incluye una comisión de administración equivalente a 40
                            UI + IVA y un seguro de vida sobre saldo deudor equivalente al 0,006 mensual sobre el saldo
                            adeudado. Las futuras cuotas del préstamo incluirán hasta 8 UI + IVA por cuota, con un tope de
                            80 UI + IVA. IVA (Impuesto Valor Agregado): 22%.</p>
                        <p className="line-h-16">En caso de que el día de vencimiento de la cuota, coincida con un feriado no
                            laborable o un domingo, el vencimiento de la cuota se traslada para el próximo día
                            hábil.</p>
                        <p className="line-h-16">Acepto el envío de parte de Emprendimientos de VALOR S.A. al número de
                            celular o al mail declarado, información relativa a esta solicitud.</p>
                        <p className="line-h-16">Emprendimientos de VALOR S.A. pone en su conocimiento que los datos
                            personales que se recaben, son requeridos en el marco de la evaluación de una futura relación
                            contractual y cumplen una finalidad meramente operativa. El carácter de los mismos es
                            obligatorio y la negativa o inexactitud, aparejará las acciones que la entidad entienda
                            correspondientes.</p>
                        <p className="line-h-16">La presente implica la aceptación del envío de parte de Emprendimientos de VALOR S.A. al número
                            de celular o al mail declarado, de información relativa a esta solicitud.
                            Asimismo, Usted autoriza expresamente a VALOR y al Banco a que sus datos personales sean
                            comunicados a BBVA, así como que sus datos en poder de BBVA puedan ser cedidos a entidades del
                            Grupo BBVA a efectos ofrecerle otros productos o servicios que podrían llegar a ser de su
                            interés. En cualquier caso usted tendrá que aceptar previa y expresamente la contratación con
                            cualquier producto.</p>
                        <p className="line-h-16">En caso que usted no quiera continuar recibiendo información sobre otros productos o servicios
                            no contratados por usted, usted podrá en cualquier momento solicitar no recibir más dicha
                            información a través del teléfono 24870083.</p>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} autoFocus>
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>

            <FormFooter
                nextButtonLabel="¡Lo quiero!"
                hideBackButton
                onNext={handleNext}
            />

            <Spinner loading={loading}/>
        </Container>
    )

}
